<template>
  <div class="content">
    <div class="course-dashboard-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-course-dashboard">

          <!-- Header ───────────────────────────────────────────── -->
          <header class="gp-head">
            <div class="gp-head__text">
              <h1 class="gp-title">Courses</h1>
              <p class="gp-sub">Build and manage the GLAD® learning content used inside pathways.</p>
            </div>
            <div class="gp-head__cta">
              <button class="gp-btn gp-btn--primary" @click="$router.push('/glad/courses/create')">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                Create course
              </button>
            </div>
          </header>

          <!-- Stat cards ───────────────────────────────────────── -->
          <section class="gp-stats">
            <div class="gp-stat">
              <div class="gp-stat__icon gp-stat__icon--violet">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
              </div>
              <div class="gp-stat__body">
                <div class="gp-stat__value">{{ stats.total }}</div>
                <div class="gp-stat__label">Total courses</div>
                <div class="gp-stat__hint">Across all statuses</div>
              </div>
            </div>
            <div class="gp-stat">
              <div class="gp-stat__icon gp-stat__icon--green">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              </div>
              <div class="gp-stat__body">
                <div class="gp-stat__value">{{ stats.published }}</div>
                <div class="gp-stat__label">Published</div>
                <div class="gp-stat__hint">Visible to learners</div>
              </div>
            </div>
            <div class="gp-stat">
              <div class="gp-stat__icon gp-stat__icon--amber">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"></path></svg>
              </div>
              <div class="gp-stat__body">
                <div class="gp-stat__value">{{ stats.drafts }}</div>
                <div class="gp-stat__label">Drafts</div>
                <div class="gp-stat__hint">Not yet published</div>
              </div>
            </div>
          </section>

          <!-- Filters bar ──────────────────────────────────────── -->
          <section class="gp-filters">
            <div class="gp-search">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gp-search__icon"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input v-model="search" type="text" placeholder="Search courses..." class="gp-search__input" @input="onSearch" />
            </div>
            <div class="gp-select">
              <select v-model="statusFilter" @change="fetchCourses">
                <option value="">All statuses</option>
                <option value="active">Published</option>
                <option value="inactive">Draft</option>
              </select>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="gp-select__chev"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
          </section>

          <!-- Loading / empty ──────────────────────────────────── -->
          <div v-if="loading" class="gp-loading">
            <span class="gp-spinner" aria-label="Loading courses"></span>
          </div>
          <div v-else-if="courses.length === 0" class="gp-empty">
            <p>No courses match your filters. <a class="gp-link" @click="$router.push('/glad/courses/create')">Create your first course →</a></p>
          </div>

          <!-- Table ────────────────────────────────────────────── -->
          <div v-else class="gp-table-wrap">
            <table class="gp-table">
              <thead>
                <tr>
                  <th class="gp-th-title">Course title</th>
                  <th>Lessons</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th class="gp-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(course, idx) in courses" :key="course.id" @click="editCourse(course.id)">
                  <td class="gp-cell-title">
                    <div class="gp-title-cell">
                      <div :class="['gp-icon-tile', iconClass(idx)]" v-html="bookSvg"></div>
                      <span class="gp-row-title">{{ course.name }}</span>
                    </div>
                  </td>
                  <td class="gp-cell-modules">{{ course.lessons_count || '—' }}</td>
                  <td>
                    <span :class="['gp-pill', course.status == 1 ? 'gp-pill--green' : 'gp-pill--gray']">
                      {{ course.status == 1 ? 'Published' : 'Draft' }}
                    </span>
                  </td>
                  <td class="gp-cell-updated">{{ formatDate(course.created_at) }}</td>
                  <td class="gp-cell-actions" @click.stop>
                    <button class="gp-icon-btn" @click="editCourse(course.id)" aria-label="Edit course">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    </button>
                    <button class="gp-icon-btn" @click="toggleMenu(course.id, $event)" aria-label="More actions">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
                    </button>
                    <div v-if="openMenuId === course.id" class="gp-menu" @click.stop>
                      <button @click="editCourse(course.id)">Edit</button>
                      <button class="gp-menu__danger" @click="confirmDelete(course)">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Delete confirm modal ─────────────────────────────── -->
          <div v-if="deleteTarget" class="gp-modal-backdrop" @click.self="deleteTarget = null">
            <div class="gp-modal">
              <h2 class="gp-modal__title">Delete course?</h2>
              <p class="gp-modal__body">
                <strong>{{ deleteTarget.name }}</strong> will be permanently removed. This cannot be undone.
              </p>
              <div class="gp-modal__actions">
                <button class="gp-btn" @click="deleteTarget = null">Cancel</button>
                <button class="gp-btn gp-btn--danger" :disabled="deleting" @click="deleteCourse">
                  {{ deleting ? 'Deleting…' : 'Delete' }}
                </button>
              </div>
            </div>
          </div>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GladCourseList',
  data() {
    return {
      loading: true,
      courses: [],
      search: '',
      statusFilter: '',
      searchTimer: null,
      deleteTarget: null,
      deleting: false,
      openMenuId: null,
      iconPalette: ['violet', 'green', 'amber', 'pink', 'indigo', 'gray'],
      bookSvg: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>',
    };
  },
  computed: {
    stats() {
      const total = this.courses.length;
      const published = this.courses.filter(c => c.status == 1).length;
      return { total, published, drafts: total - published };
    },
  },
  created() {
    this.fetchCourses();
  },
  mounted() {
    document.addEventListener('click', this.closeMenus);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenus);
  },
  methods: {
    fetchCourses() {
      this.loading = true;
      this.$http.post('course/all_courses', {
        page: 1,
        per_page: 200,
        company_id: '',
        search: this.search,
        course_status: this.statusFilter,
      })
        .then(resp => {
          // all_courses returns { courses: [...], total: N } OR an array directly
          const raw = Array.isArray(resp.data) ? resp.data : (resp.data.courses || []);
          this.courses = raw;
        })
        .catch(() => { this.courses = []; })
        .finally(() => { this.loading = false; });
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.fetchCourses, 350);
    },
    iconClass(idx) {
      return 'gp-icon-tile--' + this.iconPalette[idx % this.iconPalette.length];
    },
    toggleMenu(id, ev) {
      if (ev) ev.stopPropagation();
      this.openMenuId = this.openMenuId === id ? null : id;
    },
    closeMenus() {
      this.openMenuId = null;
    },
    editCourse(id) {
      this.$router.push('/glad/courses/' + id + '/edit');
    },
    confirmDelete(course) {
      this.openMenuId = null;
      this.deleteTarget = course;
    },
    deleteCourse() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      this.$http.delete('course/delete/' + this.deleteTarget.id)
        .then(() => {
          this.courses = this.courses.filter(c => c.id !== this.deleteTarget.id);
          this.deleteTarget = null;
        })
        .catch(() => { this.$toast && this.$toast.error('Delete failed. The course may be in use.', { timeout: 4000 }); })
        .finally(() => { this.deleting = false; });
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.course-dashboard-wrap { width: 100%; }
.course-dashboard-wrap ::v-deep > .card.no-border-card { border: 0; box-shadow: none; background: transparent; }
.glad-course-dashboard {
  padding: 1.75rem 2rem 2.5rem;
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  color: #1b1e25;
  background: #f8f8f5;
  min-height: 100%;
}

/* Header ──────────────────────────────────────────────────── */
.gp-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.25rem;
  margin-bottom: 1.75rem;
  flex-wrap: wrap;
}
.gp-title {
  font-family: 'Fraunces', 'Source Serif Pro', Georgia, serif;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: #0f1115;
  margin: 0 0 0.35rem;
  line-height: 1.1;
}
.gp-sub { color: #5b6068; font-size: 0.95rem; margin: 0; }
.gp-head__cta { display: flex; gap: 0.6rem; }

.gp-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.05rem;
  border-radius: 10px;
  font-family: var(--glad-font-sans);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #d2cfc6;
  background: #fff;
  color: #0f1115;
  line-height: 1;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 1px 1px rgba(15, 17, 21, 0.04);

  &:hover { border-color: #0f1115; }

  &.gp-btn--primary {
    background: #0f1115 !important;
    color: #ffffff !important;
    border-color: #0f1115 !important;
    &:hover { background: #1b1e25 !important; border-color: #1b1e25 !important; }
  }
  &.gp-btn--danger {
    background: #b91c1c !important;
    color: #fff !important;
    border-color: #b91c1c !important;
    &:hover { background: #991b1b !important; border-color: #991b1b !important; }
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

/* Stat cards ──────────────────────────────────────────────── */
.gp-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 760px) { grid-template-columns: 1fr; }
}
.gp-stat {
  background: #fff;
  border: 1px solid var(--glad-line);
  border-radius: 14px;
  padding: 1.1rem 1.2rem;
  display: flex;
  gap: 0.95rem;
  align-items: center;
}
.gp-stat__icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--violet { background: #ede9fe; color: #6d28d9; }
  &--green  { background: #dcfce7; color: #15803d; }
  &--amber  { background: #fef3c7; color: #b45309; }
}
.gp-stat__value {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
  color: #0f1115;
}
.gp-stat__label { font-size: 0.85rem; font-weight: 600; color: #0f1115; margin-top: 0.15rem; }
.gp-stat__hint { font-size: 0.75rem; color: #8a8f98; margin-top: 0.1rem; }

/* Filters bar ─────────────────────────────────────────────── */
.gp-filters {
  display: flex;
  gap: 0.6rem;
  margin-bottom: 0.9rem;
  flex-wrap: wrap;
  align-items: center;
}
.gp-search { flex: 1; min-width: 220px; position: relative; }
.gp-search__icon {
  position: absolute;
  left: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--glad-text-muted);
  pointer-events: none;
}
.gp-search__input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.6rem 0.85rem 0.6rem 2.25rem;
  border: 1px solid #e6e4dd;
  border-radius: 10px;
  background: #fff;
  font-size: 0.88rem;
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  color: #0f1115;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus { border-color: #0f1115; box-shadow: 0 0 0 3px rgba(15,17,21,0.06); }
}
.gp-select {
  position: relative;
  select {
    appearance: none;
    -webkit-appearance: none;
    padding: 0.6rem 2rem 0.6rem 0.85rem;
    border: 1px solid #e6e4dd;
    border-radius: 10px;
    background: #fff;
    font-size: 0.88rem;
    font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
    color: #0f1115;
    cursor: pointer;
    min-width: 160px;
    outline: none;
    &:focus { border-color: #0f1115; }
  }
}
.gp-select__chev {
  position: absolute;
  right: 0.7rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--glad-text-muted);
}

/* Loading / empty ─────────────────────────────────────────── */
.gp-loading { display: flex; justify-content: center; padding: 3rem 0; }
.gp-spinner {
  width: 32px; height: 32px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: gp-spin 0.7s linear infinite;
}
@keyframes gp-spin { to { transform: rotate(360deg); } }
.gp-empty { text-align: center; padding: 3rem 0; color: var(--glad-text-soft); }
.gp-link { color: var(--glad-accent); cursor: pointer; text-decoration: underline; }

/* Table ───────────────────────────────────────────────────── */
.gp-table-wrap {
  background: #fff;
  border: 1px solid var(--glad-line);
  border-radius: 14px;
  overflow: hidden;
}
.gp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th, td {
    text-align: left;
    padding: 0.85rem 1rem;
    vertical-align: middle;
  }
  thead th {
    background: #fbfaf6;
    color: var(--glad-text-soft);
    font-weight: 600;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    border-bottom: 1px solid var(--glad-line);
    user-select: none;
  }
  tbody tr {
    border-bottom: 1px solid var(--glad-line);
    cursor: pointer;
    transition: background 0.12s ease;
    &:hover { background: #fbfaf6; }
    &:last-child { border-bottom: 0; }
  }
  .gp-cell-modules,
  .gp-cell-updated { color: #5b6068; white-space: nowrap; }
  .gp-th-actions, .gp-cell-actions { width: 110px; }
}
.gp-title-cell { display: flex; align-items: center; gap: 0.85rem; }
.gp-row-title { font-weight: 600; color: #0f1115; }

/* Icon tiles ──────────────────────────────────────────────── */
.gp-icon-tile {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--violet { background: #ede9fe; color: #6d28d9; }
  &--green  { background: #d1fae5; color: #047857; }
  &--amber  { background: #fef3c7; color: #b45309; }
  &--pink   { background: #fce7f3; color: #be185d; }
  &--indigo { background: #e0e7ff; color: #4338ca; }
  &--gray   { background: #f1f1ee; color: #5b6068; }
}

/* Pills ───────────────────────────────────────────────────── */
.gp-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;

  &--green { background: #dcfce7; color: #15803d; }
  &--gray  { background: #f1f1ee; color: #5b6068; }
}

/* Action buttons ─────────────────────────────────────────── */
.gp-cell-actions { position: relative; white-space: nowrap; }
.gp-icon-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e6e4dd;
  border-radius: 8px;
  background: #fff;
  color: #5b6068;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
  & + & { margin-left: 0.35rem; }
  &:hover { border-color: #0f1115; color: #0f1115; }
}
.gp-menu {
  position: absolute;
  right: 0.5rem;
  top: 100%;
  margin-top: 4px;
  background: #fff;
  border: 1px solid var(--glad-line);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15,17,21,0.08);
  min-width: 140px;
  padding: 0.35rem;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  button {
    text-align: left;
    padding: 0.5rem 0.6rem;
    background: transparent;
    border: 0;
    border-radius: 6px;
    font-size: 0.83rem;
    color: var(--glad-ink);
    cursor: pointer;
    &:hover { background: var(--glad-paper-2); }
    &.gp-menu__danger { color: var(--glad-danger); }
  }
}

/* Delete modal ────────────────────────────────────────────── */
.gp-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 17, 21, 0.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}
.gp-modal {
  background: #fff;
  border: 1px solid var(--glad-line);
  border-radius: 14px;
  padding: 1.75rem;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 24px 60px rgba(15, 17, 21, 0.22);
}
.gp-modal__title { font-family: var(--glad-font-serif); font-size: 1.3rem; color: var(--glad-ink); margin: 0 0 0.6rem; }
.gp-modal__body { color: var(--glad-text-soft); margin: 0 0 1.5rem; font-size: 0.92rem; line-height: 1.5; }
.gp-modal__actions { display: flex; gap: 0.6rem; justify-content: flex-end; }
</style>
